import React, { useState, useEffect } from 'react';
import { MdSend, MdCloudUpload } from 'react-icons/md';
import Swal from 'sweetalert2';
import './VendorPortalDemo.css';
import { formatDate } from '../utils/dateUtils';

const VendorPortalDemo = () => {
    // Generate 80 Dummy Items
    const generateItems = () => {
        const baseItems = [
            { name: "Cement - Grade 53", desc: "UltraTech Premium Portland Pozzolana Cement for high durability concrete works.", size: "50kg", qty: 100, baseRate: 380 },
            { name: "Steel TMT Bars", desc: "Fe500D Grade TMT Bars, highly ductile and earthquake resistant for structural safety.", size: "12mm", qty: 250, baseRate: 65 },
            { name: "Red Bricks", desc: "Standard Clay Bricks, First Class quality, perfectly burnt and sharp edges.", size: "9x4x3", qty: 5000, baseRate: 8.5 },
            { name: "River Sand", desc: "Fine quality river sand, free from silt and organic impurities, ideal for plastering.", size: "Cu. Ft", qty: 200, baseRate: 60 },
            { name: "Teak Wood", desc: "Premium Burma Teak Wood, Grade A, seasoned and treated for termite resistance.", size: "Sq. Ft", qty: 50, baseRate: 850 },
            { name: "Ceramic Tiles", desc: "2x2 Vitrified Double Charged Styles, Glossy finish, White base with marble texture.", size: "Box", qty: 120, baseRate: 450 },
            { name: "Paint (Emulsion)", desc: "Asian Paints Royale Luxury Emulsion, stain resistant and washable finish.", size: "20L Bucket", qty: 15, baseRate: 4200 },
            { name: "Electrical Wire", desc: "Finolex Flame Retardant (FR) PVC Insulated Industrial Cables.", size: "90m Roll", qty: 30, baseRate: 1200 }
        ];

        let items = [];
        for (let i = 0; i < 80; i++) {
            const template = baseItems[i % baseItems.length];
            items.push({
                id: i + 1,
                itemName: template.name, // Unique name to track
                description: template.desc,
                size: template.size,
                orderQty: template.qty + (i * 2), // Varying qty
                rate: template.baseRate,
                total: (template.qty + (i * 2)) * template.baseRate
            });
        }
        return items;
    };

    const [items, setItems] = useState(generateItems());
    const [grandTotal, setGrandTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");

    // Filter items based on search
    const filteredItems = items.filter(item =>
        item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleRateChange = (id, newRate) => {
        const rate = parseFloat(newRate) || 0;
        setItems(prevItems => prevItems.map(item => {
            if (item.id === id) {
                return { ...item, rate: rate, total: item.orderQty * rate };
            }
            return item;
        }));
    };

    useEffect(() => {
        const total = items.reduce((sum, item) => sum + item.total, 0);
        setGrandTotal(total);
    }, [items]);

    const handleSubmit = async () => {
        await Swal.fire({
            title: 'Demo Submission',
            text: `Submitting ${items.length} items with Grand Total: ${grandTotal.toLocaleString()}`,
            icon: 'info',
            confirmButtonText: 'Got it',
            confirmButtonColor: '#4f46e5'
        });
    };

    return (
        <div className="vendor-portal-demo-wrapper">
            <div className="vp-demo-container">
                <header className="vp-demo-header">
                    <div className="vp-demo-logo">
                        <MdCloudUpload size={32} />
                        <span>Inceppt Vendor Portal (Demo)</span>
                    </div>
                    <div className="vp-demo-po-badge">PO-DEMO-2026</div>
                </header>

                <main className="vp-demo-content">
                    <section className="vp-demo-info-section">
                        <div className="vp-demo-info-grid">
                            <div className="vp-demo-info-item">
                                <label>Vendor</label>
                                <span>Demo Supplier Corp</span>
                            </div>
                            <div className="vp-demo-info-item">
                                <label>Date</label>
                                <span>{formatDate(new Date())}</span>
                            </div>
                        </div>
                    </section>

                    <section className="vp-demo-items-section">
                        <div className="vp-demo-section-header">
                            <h3>Item Details & Pricing</h3>
                            <input
                                type="text"
                                placeholder="Search items..."
                                className="vp-demo-search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Desktop Table View */}
                        <div className="vp-demo-table-container vp-desktop-only">
                            <table className="vp-demo-items-table">
                                <thead>
                                    <tr>
                                        <th>Item Name & Description</th>
                                        <th>Spec/Size</th>
                                        <th>Order Qty</th>
                                        <th>Your Rate</th>
                                        <th>Line Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map(item => (
                                        <tr key={item.id}>
                                            <td>
                                                <div className="vp-demo-item-name">{item.itemName}</div>
                                                <div className="vp-demo-item-meta">{item.description}</div>
                                            </td>
                                            <td>{item.size}</td>
                                            <td>{item.orderQty}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={item.rate}
                                                    onChange={(e) => handleRateChange(item.id, e.target.value)}
                                                    className="vp-demo-rate-input"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                />
                                            </td>
                                            <td className="vp-demo-item-total">
                                                {item.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Compact List View */}
                        <div className="vp-demo-mobile-list vp-mobile-only">
                            {filteredItems.map(item => (
                                <div key={item.id} className="vp-mobile-item">
                                    <div className="vp-mobile-row-top">
                                        <span className="vp-mobile-name">{item.itemName}</span>
                                    </div>
                                    <div className="vp-mobile-row-desc">
                                        <p>{item.description}</p>
                                    </div>

                                    <div className="vp-mobile-row-btm">
                                        <div className="vp-mobile-specs">
                                            <span className="vp-mobile-size">Size: {item.size} | Qty: {item.orderQty}</span>
                                        </div>
                                        <div className="vp-mobile-input-group">
                                            <span className="vp-currency-symbol">₹</span>
                                            <input
                                                type="number"
                                                value={item.rate}
                                                onChange={(e) => handleRateChange(item.id, e.target.value)}
                                                className="vp-mobile-rate-input"
                                                placeholder="Rate"
                                            />
                                        </div>
                                    </div>

                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="vp-demo-footer">
                        <div className="vp-demo-summary">
                            <div className="vp-demo-grand-total">
                                <label>Grand Total</label>
                                <span>{grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                        </div>
                        <button
                            className="vp-demo-submit-btn"
                            onClick={handleSubmit}
                        >
                            <MdSend /> Submit Updated Quote
                        </button>
                    </section>
                </main>

                <footer className="vp-demo-site-footer">
                    <p>&copy; {new Date().getFullYear()} Weborative. All rights reserved.</p>
                    <p>Internal Secure Portal for Vendors (Demo Mode)</p>
                </footer>
            </div>
        </div>
    );
};

export default VendorPortalDemo;
